import QRCode from "qrcode";
import Modal from "../templates/modal.handlebars";
import { Config } from "./Config.js";
import { WidgetState } from "./WidgetState.js";
import { ApiService } from "./ApiService.js";
import { WidgetController } from "./WidgetController.js";

export class ETAWidget {
    constructor() {
        if (ETAWidget.instance) return ETAWidget.instance;
        const script = document.getElementById("eta-buy-button");

        if (script == null) {
            throw Error("widget.js script must have id set to 'eta-widget'.");
        }

        new Config(script);

        if (document.getElementById(Config.get("widgetElementId")) != null) {
            throw new Error(`element id ${Config.get("widgetElementId")} has already been defined. Set the data-root-element attribute to a unique id.`);
        }

        this._setupDOM();

        ETAWidget.instance = this;
        return this;
    }

    // -------------------------------------------------------------------------
    // DOM setup
    // -------------------------------------------------------------------------

    _setupDOM() {
        const getIndex = (element) => parseInt(window.getComputedStyle(element).zIndex) || 0;
        const topIndex = () => {
            let top = 0;
            for (const element of Array.from(document.querySelectorAll("*"))) {
                const index = getIndex(element);
                if (index >= top) top = index + 1;
            }
            return top;
        };

        const fonts = document.createElement("link");
        fonts.rel = "stylesheet";
        fonts.type = "text/css";
        fonts.href = Config.get("fontsUrl");
        this.head().appendChild(fonts);

        const parent = this.body().appendChild(document.createElement("div"));
        parent.id = Config.get("widgetElementId");
        parent.style.all = "initial";
        parent.style.position = "fixed";
        parent.style.zIndex = topIndex();

        const brand = document.createElement("link");
        brand.rel = "stylesheet";
        brand.type = "text/css";
        brand.href = Config.get("brandUrl");

        const styles = document.createElement("link");
        styles.rel = "stylesheet";
        styles.type = "text/css";
        styles.href = Config.get("stylesheetUrl");

        this.shadow = parent.attachShadow({ mode: "open" });
        this.shadow.appendChild(brand);
        this.shadow.appendChild(styles);

        this.loadingContainer = this.shadow.appendChild(document.createElement("div"));
        this.loadingContainer.classList.add("loading");

        const loadingContent = this.loadingContainer.appendChild(document.createElement("div"));
        loadingContent.classList.add("loading-content");

        const svg = loadingContent.appendChild(document.createElement("svg"));
        svg.classList.add("loading-spinner");
        svg.setAttribute("viewBox", "0 0 50 50");

        const circle = svg.appendChild(document.createElement("circle"));
        circle.classList.add("path");
        circle.setAttribute("cx", "25");
        circle.setAttribute("cy", "25");
        circle.setAttribute("r", "20");
        circle.setAttribute("fill", "none");
        circle.setAttribute("stroke-width", "5");

        this.loadingText = loadingContent.appendChild(document.createElement("p"));

        this.widgetElement = document.createElement("div");
        this.shadow.appendChild(this.widgetElement);
    }

    // -------------------------------------------------------------------------
    // Document helpers
    // -------------------------------------------------------------------------

    body() { return document.getElementsByTagName("body")[0]; }
    head() { return document.getElementsByTagName("head")[0]; }

    getElementById(element) { return this.shadow.getElementById(element) }
    querySelectorAll(query) { return this.shadow.querySelectorAll(query) }
    querySelector(query) { return this.shadow.querySelector(query) }

    print() {
        window.print();
    }

    // -------------------------------------------------------------------------
    // Mount: bind all trigger buttons on the host page
    // -------------------------------------------------------------------------

    mount() {
        const triggerClass = Config.get("targetButtonClass");
        for (const button of document.querySelectorAll(`.${triggerClass}`)) {
            const eventId = button.dataset.eventId;
            if (eventId == null) {
                throw Error(`All buttons with class ${triggerClass} must have a data-event-id attribute`);
            }
            this.bindCartButton(button);
        }
    }

    bindCartButton(button) {
        const listener = () => {
            const promoCode = WidgetState.getUrlPromoCode() || button.dataset.promoCode;
            const program = button.dataset.program || Config.get("program");

            this.showLoading(true);
            this.load(button.dataset.eventId, program, promoCode)
                .then(() => {
                    this.showLoading(false);
                    this.showCart();
                })
                .catch((error) => {
                    console.log(error)
                    if (error === 404) {
                        this.showLoadingFailed("We could not find this event");
                    } else if (error == 410) {
                        this.showLoadingFailed("Sales have ended for this event");
                    } else if (error == 422) {
                        this.showLoadingFailed("All seats are sold out");
                    } else {
                        const block = Config.handleError(button.dataset.eventId);
                        if (block === true) {
                            return;
                        }
                        this.showLoadingFailed("Something went wrong");
                    }
                });
        };
        button.addEventListener("click", listener.bind(this));
    }

    // -------------------------------------------------------------------------
    // Data loading
    // -------------------------------------------------------------------------

    async load(eventId, program=null, promoCode=null) {
        try {
            const response = await ApiService.getEvent(eventId, program);
            if (response.ok) {
                const data = await response.json();
                data.schedules.sort((a, b) => {
                    const dateA = new Date(a.date ?? a.stations?.[0]?.shuttles?.[0]?.date ?? 0);
                    const dateB = new Date(b.date ?? b.stations?.[0]?.shuttles?.[0]?.date ?? 0);
                    return dateA - dateB;
                });
                this.widgetElement.innerHTML = Modal(data);
                this.widgetController = new WidgetController(this);
                this.widgetController.load(data);
                if (promoCode != null) {
                    await this.widgetController.applyDiscount(promoCode);
                }
            } else {
                throw response.status;
            }
        } catch (error) {
            throw error;
        }
    }

    async loadSession(sessionId) {
        console.info("load session ", sessionId);
    }

    async loadTickets(orderNumber) {
        const isAndroid = () => /android/i.test(navigator.userAgent);
        const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

        try {
            const resp = await ApiService.getTickets(orderNumber);
            const data = await resp.json();
            this.widgetElement.innerHTML = Modal(data);
            this.widgetController = new WidgetController(this);

            if (isIOS()) {
                this.downloadIOSSections().forEach(section => section.classList.remove("hidden"));
            } else if (isAndroid()) {
                this.downloadAndroidSections().forEach(section => section.classList.remove("hidden"));
            } else {
                await QRCode.toCanvas(this.mobileQRCanvas(), window.location.href);
                for (const canvas of this.barcodeCanvases()) {
                    const barcode = canvas.dataset.barcode;
                    await QRCode.toCanvas(canvas, barcode);
                }
                const printFunction = this.print.bind(this);
                this.mobileQRSection().classList.remove("hidden");
                this.printSection().classList.remove("hidden");
                this.getElementById("print").addEventListener("click", printFunction);
                this.barcodeSections().forEach(section => section.classList.remove("hidden"));
            }
            this.downloadTicketDataSection().classList.remove("hidden");
            this.showDownload();

        } catch (e) {
            console.error("error caught");
            console.error(e);
            this.showLoadingFailed("Tickets not found");
        }
    }

    // -------------------------------------------------------------------------
    // Event binding
    // -------------------------------------------------------------------------

    bindQuantityChangeListener(addListener, removeListener) {
        const add = this.querySelectorAll("button.stepper[data-action='add']");
        const subtract = this.querySelectorAll("button.stepper[data-action='remove']");

        for (const button of subtract) {
            button.removeEventListener("click", this.removeListener);
            button.addEventListener("click", removeListener);
        }
        for (const button of add) {
            button.removeEventListener("click", this.addListener);
            button.addEventListener("click", addListener);
        }

        this.addListener = addListener;
        this.removeListener = removeListener;
    }

    bindLocationDetailsListener(clickListener) {
        const buttons = this.querySelectorAll("button[data-action='location-details']");
        for (const button of buttons) {
            button.removeEventListener("click", this.locationDetailsListener);
            button.addEventListener("click", clickListener);
        }
        this.locationDetailsListener = clickListener;
    }

    bindDaySelectListener(changeListener) {
        if (this.getSelectDayForm() == null) return;
        if (this.dayChangeListener) {
            this.getSelectDayForm().removeEventListener("change", this.dayChangeListener);
        }
        this.dayChangeListener = changeListener;
        this.getSelectDayForm().addEventListener("change", this.dayChangeListener);
    }

    bindBuildPassListener(clickListener) {
        if (this.getBuildPassButton() == null) return;
        if (this.buildPassListener) {
            this.getBuildPassButton().removeEventListener("click", this.buildPassListener);
        }
        this.buildPassListener = clickListener;
        this.getBuildPassButton().addEventListener("click", this.buildPassListener);
    }

    bindConfigureDayStationListener(changeListener) {
        const forms = this.getConfigurePassStationForms();
        if (this.stationChangeListener) {
            for (const form of forms) {
                form.removeEventListener("change", this.stationChangeListener);
            }
        }
        this.stationChangeListener = changeListener;
        for (const form of forms) {
            form.addEventListener("change", this.stationChangeListener);
        }
    }

    bindConfigureDayShuttleListener(changeListener) {
        const forms = this.getConfigurePassShuttleForms();
        if (this.shuttleChangeListener) {
            for (const form of forms) {
                form.removeEventListener("change", this.shuttleChangeListener);
            }
        }
        this.shuttleChangeListener = changeListener;
        for (const form of forms) {
            form.addEventListener("change", this.shuttleChangeListener);
        }
    }

    bindConfigureDayEditButtonListener(listener) {
        if (this.getConfigurePassEditButtons().length === 0) return;
        const buttons = this.getConfigurePassEditButtons();
        if (this.configureDayEditListener) {
            for (const button of buttons) {
                button.removeEventListener("click", this.configureDayEditListener);
            }
        }
        this.configureDayEditListener = listener;
        for (const button of buttons) {
            button.addEventListener("click", this.configureDayEditListener);
        }
    }

    bindConfigureDayCancelEditButtonListener(listener) {
        if (this.getConfigurePassCancelEditButtons().length === 0) return;
        const buttons = this.getConfigurePassCancelEditButtons();
        if (this.configureDayCancelEditListener) {
            for (const button of buttons) {
                button.removeEventListener("click", this.configureDayCancelEditListener);
            }
        }
        this.configureDayCancelEditListener = listener;
        for (const button of buttons) {
            button.addEventListener("click", this.configureDayCancelEditListener);
        }
    }

    bindCheckoutButton(listener) {
        const button = this.checkoutButton();
        button.removeEventListener("click", this.checkoutListener);
        button.addEventListener("click", listener);
        this.checkoutListener = listener;
    }

    bindBackButton(listener) {
        const buttons = this.backButtons();
        buttons.forEach((button) => {
            button.removeEventListener("click", this.backListener);
            button.addEventListener("click", listener);
        });
        this.backListener = listener;
    }

    bindCloseButton(listener) {
        for (const button of this.closeButtons()) {
            button.removeEventListener("click", this.closeListener);
            button.addEventListener("click", listener);
        }
        this.closeListener = listener;
    }

    // -------------------------------------------------------------------------
    // DOM accessors — checkout & payment
    // -------------------------------------------------------------------------

    paymentSection() { return this.querySelector("div[data-section='payment']") }
    checkoutButton() { return this.querySelector("button[data-action='checkout']") }
    checkoutMessage() { return this.querySelector("div.checkoutError") }
    closeButtons() { return this.querySelectorAll("button[data-action='close-modal']") }
    backButtons() { return this.querySelectorAll("button[data-action='back']") }

    // -------------------------------------------------------------------------
    // DOM accessors — cart / line items
    // -------------------------------------------------------------------------

    getCartSection() { return this.getElementById("cartSummary"); }
    getLineItems() { return this.querySelectorAll(".lineitem") }
    getLineItem(shuttleId) { return this.querySelector(`.lineitem[data-shuttle="${shuttleId}"]`) }

    // -------------------------------------------------------------------------
    // DOM accessors — modals
    // -------------------------------------------------------------------------

    cartModal() { return this.getElementById("cartModal") }
    checkoutModal() { return this.getElementById("checkoutModal") }
    downloadModal() { return this.getElementById("downloadModal") }
    locationModal() { return this.getElementById("locationModal") }

    // -------------------------------------------------------------------------
    // DOM accessors — ticket download
    // -------------------------------------------------------------------------

    downloadIOSSections() { return this.querySelectorAll("div[data-section='downloadIOS']") }
    downloadAndroidSections() { return this.querySelectorAll("div[data-section='downloadAndroid']") }
    barcodeSections() { return this.querySelectorAll("div[data-section='barcode']") }
    barcodeCanvases() { return this.querySelectorAll("canvas.barcode") }
    mobileQRSection() { return this.querySelector("div[data-section='downloadQRCode']") }
    mobileQRCanvas() { return this.getElementById("mobileCode") }
    downloadTicketDataSection() { return this.querySelector("div[data-section='downloadTicketData']") }
    printSection() { return this.querySelector("div[data-section='print']") }

    // -------------------------------------------------------------------------
    // DOM accessors — single-day / multi-day pass selection
    // -------------------------------------------------------------------------

    getSelectDayForm() { return this.querySelector("#select-day-form") }
    getSingleDaySection() { return this.querySelector("div[data-section='day-pass']") }
    getCurrentShuttleSections() { return this.querySelectorAll("div[data-section='select-shuttle']:not(.hidden)") }
    getShuttleSections(scheduleId) { return this.querySelectorAll(`div[data-section='select-shuttle'][data-schedule-id='${scheduleId}']`) }

    // -------------------------------------------------------------------------
    // DOM accessors — configure multi-day pass
    // -------------------------------------------------------------------------

    getBuildPassButton() { return this.querySelector("button[data-action='configure-pass']") }
    getConfigurePassSection() { return this.querySelector("div[data-section='configure-multiday-pass']") }
    getConfigurePassStationForms() { return this.querySelectorAll(`form[id^='select-station-form-']`) }
    getConfigurePassStationForm(scheduleId) { return this.querySelector(`form[id^='select-station-form-${scheduleId}']`) }
    getSelectedConfigurePassStation(scheduleId) { return this.querySelector(`input[type="radio"][name="${scheduleId}"]:checked`) }
    getConfigurePassShuttleForms() { return this.querySelectorAll(`form[id^='select-shuttle-form-']`) }
    getConfigurePassShuttleForm(scheduleId) { return this.querySelector(`form[id^='select-shuttle-form-${scheduleId}']`) }
    getSelectedConfigurePassShuttle(scheduleId) { return this.querySelector(`input[type="radio"][name="schedule-${scheduleId}"]:checked`) }
    getConfigurePassShuttleInputs(scheduleId) { return this.querySelectorAll(`input[data-schedule-id='${scheduleId}']`) }
    getConfigurePassShuttleLabels(scheduleId) { return this.querySelectorAll(`label[data-schedule-id='${scheduleId}']`) }
    getConfigurePassShuttleStationInputs(stationId) { return this.querySelectorAll(`input[data-station-id='${stationId}']`) }
    getConfigurePassShuttleStationLabels(stationId) { return this.querySelectorAll(`label[data-station-id='${stationId}']`) }
    getConfigurePassStationDepartureLabel(scheduleId) { return this.querySelector(`div[data-section="choose-departure-time"][data-schedule-id="${scheduleId}"]`); }
    getConfigurePassEditButtons() { return this.querySelectorAll("button[data-action='edit-day']") }
    getConfigurePassEditButton(scheduleId) { return this.querySelector(`button[data-action='edit-day'][data-schedule-id='${scheduleId}']`) }
    getConfigurePassCancelEditButtons() { return this.querySelectorAll(`button[data-action='cancel-edit-day']`) }
    getConfigurePassCancelEditButton(scheduleId) { return this.querySelector(`button[data-action='cancel-edit-day'][data-schedule-id='${scheduleId}']`) }
    getConfigurePassConfigurationSection(scheduleId) { return this.querySelector(`div[data-section="day-configuration"][data-schedule-id="${scheduleId}"]`) }
    getConfigurePassSummarySection(scheduleId) { return this.querySelector(`div[data-section="day-summary"][data-schedule-id="${scheduleId}"]`) }
    setConfigurePassSummaryMessage(scheduleId, message) { this.querySelector(`div[data-section="day-summary"][data-schedule-id="${scheduleId}"] > p`).textContent = message }
    getConfigurePassShuttleName(scheduleId) { return this.getSelectedConfigurePassShuttle(scheduleId)?.dataset.name || null; }
    getConfigurePassStationName(scheduleId) { return this.getSelectedConfigurePassStation(scheduleId)?.dataset.name || null; }
    getDiscountCode() { return this.querySelector("div[data-section='discount']")?.dataset.promoCode || null; }
    getPromoCodeInput() { return this.querySelector("#promo-code") }

    // -------------------------------------------------------------------------
    // UI state — line items & checkout button
    // -------------------------------------------------------------------------

    setLineItemQuantity(shuttleId, quantity) {
        const lineitem = this.getLineItem(shuttleId);
        lineitem.dataset.quantity = parseInt(quantity);
        lineitem.querySelector("p.stepper").textContent = quantity;
        this.updateCheckoutButton();
    }

    updateCheckoutButton() {
        const numItems = Array.from(this.getLineItems()).reduce((total, lineItem) => {
            return total + parseInt(lineItem.dataset.quantity);
        }, 0);
        (numItems > 0) ? this.enableCheckoutButton() : this.disableCheckoutButton();
    }

    enableCheckoutButton() {
        const button = this.checkoutButton();
        button.removeAttribute("disabled");
        button.classList.remove("disabled:opacity-50", "disabled:cursor-not-allowed");
    }

    disableCheckoutButton() {
        const button = this.checkoutButton();
        button.setAttribute("disabled", "");
        button.classList.add("disabled:opacity-50", "disabled:cursor-not-allowed");
    }

    setCheckoutButtonLoading(loading = true, error=null) {
        const button = this.checkoutButton();
        const message = this.checkoutMessage();

        message.textContent = error;
        (error == null) ? message.classList.add("hidden") : message.classList.remove("hidden");

        const buttonText = button.querySelector("span");
        if (loading == true) {
            button.classList.add("button-loading");
            buttonText.classList.add("hidden");
        } else {
            button.classList.remove("button-loading");
            buttonText.classList.remove("hidden");
        }
    }

    // -------------------------------------------------------------------------
    // UI state — pass configuration
    // -------------------------------------------------------------------------

    setPassConfiguration() {
        const lineitem = this.getLineItem("full-pass");
        const configuration = this.getFullPassConfiguration();
        lineitem.dataset.pass = configuration.join();
    }

    getFullPassConfiguration() {
        const selections = this.querySelectorAll("input[data-item='pass-shuttle']:checked");
        const shuttles = [];
        for (const selection of [...selections]) {
            shuttles.push(selection.value);
        }
        return shuttles;
    }

    updateFulldayControls() {
        if (this.getConfigurePassEditButtons().length === 0) return;
        const buttons = this.getConfigurePassEditButtons();
        let isPassConfigured = true;
        for (const button of buttons) {
            if (button.classList.contains("hidden")) {
                isPassConfigured = false;
            }
        }
        this.enableAddPassControl(isPassConfigured);
    }

    enableAddPassControl(enable) {
        const controls = this.querySelector("div[data-section='add-pass-to-cart']");
        const buildButton = this.getBuildPassButton();
        if (controls) {
            if (enable == true) {
                buildButton.classList.add("hidden");
                controls.classList.remove("hidden")
            } else {
                buildButton.classList.remove("hidden");
                controls.classList.add("hidden")
            }
        }
    }

    // -------------------------------------------------------------------------
    // UI state — modal visibility
    // -------------------------------------------------------------------------

    showCart() {
        this.cartModal().classList.remove("hidden");
        this.freezePage(true);
    }

    hideCart() { this.cartModal().classList.add("hidden") }

    showCheckout() {
        this.hideCart();
        this.checkoutModal().classList.remove("hidden");
    }

    hideCheckout() { this.checkoutModal().classList.add("hidden") }

    getLocations() { return this.querySelectorAll("div[data-section='location']") }

    showLocation(venueId) {
        this.locationModal().classList.remove("hidden");
        this.hideCart();
        this.getLocations().forEach((location) => {
            (location.dataset.venueId === venueId) ? location.classList.remove("hidden") : location.classList.add("hidden");
        });          
    }

    hideLocation() { 
        this.showCart();
        this.locationModal().classList.add("hidden");
        this.getLocations().forEach((location) => {
            location.classList.add("hidden");
        });
    }

    showDownload() {
        this.downloadModal().classList.remove("hidden");
        this.freezePage(true);
    }

    closeCart() {
        this.cartModal().classList.add("hidden");
        this.checkoutModal().classList.add("hidden");
        this.downloadModal().classList.add("hidden");
        this.locationModal().classList.add("hidden");
        this.freezePage(false);
    }

    hideBackButtons() { 
        this.backButtons().forEach((button) => {
            button.classList.add("hidden");
        });
    }

    // -------------------------------------------------------------------------
    // UI state — loading overlay
    // -------------------------------------------------------------------------

    showLoading(isLoading, message="Finding Availability ...") {
        if (isLoading == true) {
            this.loadingContainer.classList.add("active");
            this.loadingText.textContent = message;
        } else {
            this.loadingText.textContent = "";
            this.loadingContainer.classList.remove("active");
        }
    }

    showLoadingFailed(message="No event found for this shuttle.") {
        this.showLoading(true, message);
        const button = document.createElement("button");
        this.loadingText.after(button);
        button.classList.add("pill", "mt-6");
        button.textContent = "Close";

        button.addEventListener("click", (event) => {
            event.target.parentNode.removeChild(event.target);
            this.showLoading(false);
        });
    }

    freezePage(freeze=true) {
        const body = this.body();
        if (freeze == true) {
            const style = window.getComputedStyle(body);
            this.originalOverflow = style.overflow;
            body.style.overflow = "hidden";
        } else if (this.originalOverflow != null) {
            body.style.overflow = this.originalOverflow;
        }
    }
}
